import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, InputNumber, Select, Switch, message, Tag, Empty, Alert, Input,
} from 'antd';
import { useListAffiliateProgramsQuery, useUpdateAffiliateProgramMutation } from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const COMMISSION_TYPE_OPTIONS = [
  { value: 'percent', label: 'Percentage' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'tiered', label: 'Tiered' },
];

export default function AffiliateProgramsPage() {
  const { data, isLoading, isError, error } = useListAffiliateProgramsQuery();
  const [updateProgram] = useUpdateAffiliateProgramMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleOpen = (program) => {
    setSelected(program);
    form.setFieldsValue({
      commission_rate: program.commission_rate != null ? Number(program.commission_rate) : null,
      commission_type: program.commission_type === 'percentage' ? 'percent' : program.commission_type,
      cookie_days: program.cookie_days,
      is_active: !!program.is_active,
      notes: typeof program.terms === 'string' ? program.terms : '',
    });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await updateProgram({ programId: selected.id, ...values }).unwrap();
      message.success('Program updated');
      setModalOpen(false);
    } catch (err) {
      message.error(err.data?.title || 'Failed to update program');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Merchant', dataIndex: 'merchant_name', key: 'merchant_name' },
    { title: 'Network', dataIndex: 'network_name', key: 'network_name' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active) => (active ? <Tag color="green">active</Tag> : <Tag color="red">inactive</Tag>),
    },
    {
      title: 'Commission',
      key: 'commission',
      width: 150,
      render: (_, r) => `${r.commission_rate ?? '—'}% (${r.commission_type || '—'})`,
    },
    { title: 'Cookie Days', dataIndex: 'cookie_days', key: 'cookie_days', width: 100 },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, r) => <Button size="small" onClick={() => handleOpen(r)}>Edit</Button>,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Affiliate Programs"
        subtitle="Commission rates and cookie windows by merchant network"
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load affiliate programs'}
        />
      )}

      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          size="middle"
          locale={{ emptyText: <Empty description="No affiliate programs" /> }}
        />
      </div>

      <Modal
        title="Edit Affiliate Program"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="commission_rate" label="Commission Rate (%)">
            <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="commission_type" label="Commission Type">
            <Select options={COMMISSION_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="cookie_days" label="Cookie Duration (days)">
            <InputNumber min={1} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notes" label="Terms / Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
